import ChatIcon from "@mui/icons-material/Chat";
import KeyboardTabIcon from "@mui/icons-material/KeyboardTab";
import Badge from "@mui/material/Badge";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { CSSObject } from "@mui/material";
import { Theme } from "@mui/material";
import MuiDrawer from "@mui/material/Drawer";
import { styled } from "@mui/material/styles";
import Forum from "messaging/forums/forum";
import ChatWindow from "messaging/forums/_views/components/forum-chat-window";

const openedMixin = (): CSSObject => ({
  overflowX: 'hidden',
});
const closedMixin = (theme: Theme): CSSObject => ({
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});
const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme }) => ({
    boxSizing: 'border-box',
    flexShrink: 0,
    variants: [
      {
        props: ({ open }) => open,
        style: {
          ...openedMixin(),
          '& .MuiDrawer-paper': openedMixin(),
        },
      },
      {
        props: ({ open }) => !open,
        style: {
          ...closedMixin(theme),
          '& .MuiDrawer-paper': closedMixin(theme),
        },
      },
    ],
    whiteSpace: 'nowrap'
  }),
);
const ChatPanel = styled(
  Drawer,
  { shouldForwardProp: (prop) => prop !== "closeAfterTransition" }
)(
  ({ theme }) => ({
    position: "fixed",
    zIndex: theme.zIndex.modal + 1,
  }));
const ChatPanelContainer = styled("div")(
  () => ({
    flex: 1,
    height: "100%",
  }));
const ChatPanelContent = styled("div")(
  ({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    height: "100%",
    padding: theme.spacing(2),
    width: "400px",
  }));
const TitleBar = styled("div")(
  ({ theme }) => ({
    flex: 0,
    alignItems: "center",
    display: "flex",
    justifyContent: "space-between",
    flexDirection: "row",
    paddingBottom: theme.spacing(1),
  }));
const ChatPanelButtonContainer = styled("div")(
  ({ theme }) => ({
    marginTop: theme.spacing(0)
  }));
const ChatPanelButton = styled(Button)(
  () => ({
    transform: "translateX(30px) rotate(90deg)",
    transformOrigin: "left center",
  }));
const ChatIconContainer = styled('div')(() => ({
  paddingBottom: 0,
  position: 'relative',
  rotate: "-90deg",
  zIndex: 0
}));
const ChatContainer = styled('div')(() => ({
  flex: 1,
  height: 0
}));

type ChatDrawerProps = {
  forums: Forum[];
  isOpen: boolean;
  isArchived?: boolean;
  onOpen: () => void;
  onClose: () => void;
};

export default function ChatDrawer(props: Readonly<ChatDrawerProps>) {
  const {
    forums,
    isOpen,
    isArchived,
    onOpen,
    onClose
  } = props;

  const unreadMessageCount = 0;

  return (
    <ChatPanel
      open={isOpen}
      anchor="right"
      variant="permanent"
      closeAfterTransition={false}
    >
      {!isOpen && (
        <ChatPanelButtonContainer>
          <ChatPanelButton
            variant="text"
            size="large"
            startIcon={
              <ChatIconContainer>
                <Badge
                  color="secondary"
                  overlap="circular"
                  variant={
                    unreadMessageCount ? "standard" : "dot"
                  }
                  badgeContent={
                    unreadMessageCount > 0
                      ? unreadMessageCount : undefined
                  }
                  invisible={false}
                >
                  <ChatIcon />
                </Badge>
              </ChatIconContainer>
            }
            onClick={onOpen}
          >
            Messages
          </ChatPanelButton>
        </ChatPanelButtonContainer>
      )}
      {isOpen && (
        <ChatPanelContainer>
          <ChatPanelContent>
            <TitleBar>
              <Typography variant="h5">
                Messages
              </Typography>
              <Tooltip title="Minimize messages panel">
                <IconButton
                  size="medium"
                  onClick={onClose}>
                  <KeyboardTabIcon fontSize="large" />
                </IconButton>
              </Tooltip>
            </TitleBar>
            <ChatContainer>
              <ChatWindow 
                forums={forums} 
                readOnly={isArchived}
                onClose={onClose}
              />
            </ChatContainer>
          </ChatPanelContent>
        </ChatPanelContainer>
      )}
    </ChatPanel>
  );
}
